import {Dispatch, SetStateAction, TouchEvent as ReactTouchEvent, useEffect, useLayoutEffect, useMemo, useRef, useState} from 'react'
import {TypedUseSelectorHook, useDispatch, useSelector} from 'react-redux'
import {selectUserInfo} from './features/account/accountSlice'
import {Role} from './models/Role'
import type {AppDispatch, RootState} from './store'

export const useAppDispatch = () => useDispatch<AppDispatch>()
export const useAppSelector: TypedUseSelectorHook<RootState> = useSelector

export interface ScreenSize{
    width: number;
    height: number;
}

export interface MediaQuery {
    xs: boolean;
    sm: boolean;
    md: boolean;
    lg: boolean;
    xl: boolean;
}

export const useScreenSize: () => ScreenSize = () => {
    const [screenSize, setScreenSize] = useState({width: 0, height : 0} as ScreenSize);

    useLayoutEffect(() => {
        function updateScreenSize() {
            setScreenSize({width: window.innerWidth, height: window.innerHeight});
        }
        window.addEventListener("resize", updateScreenSize);
        updateScreenSize();
        return () => window.removeEventListener("resize", updateScreenSize);
    }, []);
    return screenSize;
}

export const useMediaQuery : () => MediaQuery = () => {
    const screenSize = useScreenSize();
    return {
        xs: screenSize.width > 0,
        sm: screenSize.width > 576,
        md: screenSize.width > 768,
        lg: screenSize.width > 992,
        xl : screenSize.width > 1200
    } as MediaQuery;
}

export const useAccessInfo: (role: Role) => boolean = (role: Role) => {
    const userInfo = useAppSelector(selectUserInfo);
    switch (role) {
        case Role.Doctor:
            return userInfo?.role === Role.Doctor || userInfo?.role === Role.Support || userInfo?.role === Role.Admin;
        case Role.Support:
            return userInfo?.role === Role.Support || userInfo?.role === Role.Admin;
        case Role.Admin:
            return userInfo?.role === Role.Admin;
        default:
            return false;
    }

};

export const useAbortController: () => [AbortController,  Dispatch<SetStateAction<AbortController>>] = () => {
    const [controller, setController] = useState(new AbortController());
    useEffect(() => {
        return () => {
            controller.abort();
        }
    }, [controller]);
    return [controller, setController];
}

/** true when the primary pointer is a finger (phones, tablets) */
export const useIsTouchDevice: () => boolean = () => {
    return useMemo(() => typeof window !== "undefined" && typeof window.matchMedia === "function"
        && window.matchMedia("(pointer: coarse)").matches, []);
}

export interface LongPressHandlers {
    onTouchStart: (event: ReactTouchEvent) => void;
    onTouchMove: (event: ReactTouchEvent) => void;
    onTouchEnd: (event: ReactTouchEvent) => void;
    onTouchCancel: () => void;
    onContextMenuCapture: () => void;
}

const LONG_PRESS_DELAY_MS = 600;
const LONG_PRESS_MOVE_TOLERANCE_PX = 10;

/**
 * Long press by finger. iOS Safari does not fire "contextmenu" on a long press, so context menus were unreachable there.
 * Android fires "contextmenu" by itself - then the timer is cancelled (onContextMenuCapture).
 * The click that follows a long press is suppressed.
 */
export const useLongPress = (onLongPress: ((x: number, y: number) => void) | null | undefined): LongPressHandlers => {
    const timer = useRef<number | null>(null);
    const start = useRef<{ x: number, y: number } | null>(null);
    const fired = useRef(false);
    useEffect(() => {
        const timerRef = timer;
        return () => {
            if (timerRef.current !== null) {
                window.clearTimeout(timerRef.current);
            }
        };
    }, []);
    const cancel = () => {
        if (timer.current !== null) {
            window.clearTimeout(timer.current);
            timer.current = null;
        }
    };
    return {
        onTouchStart: (event) => {
            fired.current = false;
            cancel();
            if (!onLongPress || event.touches.length !== 1) {
                return;
            }
            const touch = event.touches[0];
            start.current = {x: touch.clientX, y: touch.clientY};
            timer.current = window.setTimeout(() => {
                timer.current = null;
                fired.current = true;
                if (start.current) {
                    onLongPress(start.current.x, start.current.y);
                }
            }, LONG_PRESS_DELAY_MS);
        },
        onTouchMove: (event) => {
            const touch = event.touches[0];
            if (start.current && touch && Math.hypot(touch.clientX - start.current.x, touch.clientY - start.current.y) > LONG_PRESS_MOVE_TOLERANCE_PX) {
                cancel();
            }
        },
        onTouchEnd: (event) => {
            cancel();
            if (fired.current) {
                // no click after the long press
                event.preventDefault();
                event.stopPropagation();
            }
        },
        onTouchCancel: cancel,
        onContextMenuCapture: cancel
    };
}
